export const MAX_MARBLE_COUNT = 100;
export const MAX_NAME_LENGTH = 16;

export interface ParticipantGroup {
  readonly name: string;
  readonly count: number;
}

export interface MarbleEntry {
  readonly id: string;
  readonly name: string;
  readonly copyIndex: number;
  readonly copyTotal: number;
}

export class ParticipantInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParticipantInputError';
  }
}

export function normalizeParticipantName(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function participantTotal(groups: readonly ParticipantGroup[]): number {
  return groups.reduce((sum, group) => sum + group.count, 0);
}

export function validateParticipantName(value: string): string {
  const name = normalizeParticipantName(value);
  if (!name) throw new ParticipantInputError('이름을 입력해 주세요.');
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u.test(name)) {
    throw new ParticipantInputError('이름에는 제어 문자를 넣을 수 없어요.');
  }
  if ([...name].length > MAX_NAME_LENGTH) {
    throw new ParticipantInputError(`이름은 ${MAX_NAME_LENGTH}자 이하로 입력해 주세요.`);
  }
  return name;
}

export function validateParticipantCount(value: number): number {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new ParticipantInputError(`수량은 1~${MAX_MARBLE_COUNT} 사이의 정수여야 해요.`);
  }
  if (value > MAX_MARBLE_COUNT) {
    throw new ParticipantInputError(`마블은 최대 ${MAX_MARBLE_COUNT}개까지 만들 수 있어요.`);
  }
  return value;
}

export function mergeParticipantGroup(
  groups: readonly ParticipantGroup[],
  draft: ParticipantGroup,
): { groups: ParticipantGroup[]; mergedIndex: number; added: number } {
  const name = validateParticipantName(draft.name);
  const count = validateParticipantCount(draft.count);
  const currentTotal = participantTotal(groups);
  if (currentTotal + count > MAX_MARBLE_COUNT) {
    throw new ParticipantInputError(`${count}개를 넣으면 100개를 넘어요. 지금은 ${MAX_MARBLE_COUNT - currentTotal}개까지 추가할 수 있어요.`);
  }
  const key = name.toLocaleLowerCase('ko-KR');
  const existingIndex = groups.findIndex((group) => group.name.toLocaleLowerCase('ko-KR') === key);
  if (existingIndex < 0) return { groups: [...groups, { name, count }], mergedIndex: groups.length, added: count };
  const next = groups.map((group, index) => index === existingIndex
    ? { ...group, count: group.count + count }
    : group);
  return { groups: next, mergedIndex: existingIndex, added: count };
}

export function updateParticipantGroup(
  groups: readonly ParticipantGroup[],
  index: number,
  draft: ParticipantGroup,
): ParticipantGroup[] {
  if (!groups[index]) throw new ParticipantInputError('수정할 참가자를 찾지 못했어요.');
  const name = validateParticipantName(draft.name);
  const count = validateParticipantCount(draft.count);
  const duplicate = groups.findIndex((group, candidate) => (
    candidate !== index && group.name.toLocaleLowerCase('ko-KR') === name.toLocaleLowerCase('ko-KR')
  ));
  if (duplicate >= 0) throw new ParticipantInputError('이미 있는 이름이에요. 수량을 조절해 주세요.');
  const nextTotal = participantTotal(groups) - groups[index].count + count;
  if (nextTotal > MAX_MARBLE_COUNT) {
    throw new ParticipantInputError(`마블은 최대 ${MAX_MARBLE_COUNT}개까지 만들 수 있어요.`);
  }
  return groups.map((group, candidate) => candidate === index ? { name, count } : group);
}

export function setAllParticipantCounts(groups: readonly ParticipantGroup[], count: number): ParticipantGroup[] {
  validateParticipantCount(count);
  if (groups.length * count > MAX_MARBLE_COUNT) {
    throw new ParticipantInputError(`${groups.length}명에게 ${count}개씩 주면 100개를 넘어요.`);
  }
  return groups.map((group) => ({ ...group, count }));
}

function parseParticipantToken(token: string): ParticipantGroup {
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u.test(token)) {
    throw new ParticipantInputError('이름에는 제어 문자를 넣을 수 없어요.');
  }
  const multiplier = token.match(/^(.*?)(?:(?:\s+[xX]\s*)|(?:\s*[×*]\s*))(\d+)$/u);
  const twoColumn = !multiplier ? token.match(/^(.*?)(?:\t+|\s{2,}|\s+)(\d+)$/u) : null;
  const invalidMultiplier = token.match(/^(.*?)(?:(?:\s+[xX]\s*)|(?:\s*[×*]\s*))(.*)$/u);
  if (!multiplier && (invalidMultiplier || /^[xX×*]\s*[+-]?\d/u.test(token))) {
    throw new ParticipantInputError('배수는 X 10처럼 1 이상의 정수로 입력해 주세요.');
  }
  const name = validateParticipantName(multiplier?.[1] ?? twoColumn?.[1] ?? token);
  const count = validateParticipantCount(Number(multiplier?.[2] ?? twoColumn?.[2] ?? 1));
  return { name, count };
}

export function parseParticipantGroups(raw: string): ParticipantGroup[] {
  const tokens = raw
    .split(/[\n,]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length === 0) {
    throw new ParticipantInputError('참가자를 두 명 이상 입력해 주세요.');
  }

  const merged = new Map<string, ParticipantGroup>();
  for (const token of tokens) {
    const { name, count } = parseParticipantToken(token);

    const key = name.toLocaleLowerCase('ko-KR');
    const previous = merged.get(key);
    merged.set(key, { name: previous?.name ?? name, count: (previous?.count ?? 0) + count });
  }

  const groups = [...merged.values()];
  const total = participantTotal(groups);
  if (total < 2) throw new ParticipantInputError('마블은 두 개 이상 필요해요.');
  if (total > MAX_MARBLE_COUNT) {
    throw new ParticipantInputError(`마블은 최대 ${MAX_MARBLE_COUNT}개까지 만들 수 있어요.`);
  }
  return groups;
}

export function expandParticipantGroups(groups: readonly ParticipantGroup[]): MarbleEntry[] {
  const entries: MarbleEntry[] = [];
  for (const group of groups) {
    for (let index = 1; index <= group.count; index += 1) {
      entries.push({
        id: `marble-${entries.length + 1}`,
        name: group.name,
        copyIndex: index,
        copyTotal: group.count,
      });
    }
  }
  return entries;
}

export function marbleDisplayName(entry: MarbleEntry): string {
  const circled = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '⑪', '⑫', '⑬', '⑭', '⑮', '⑯', '⑰', '⑱', '⑲', '⑳'];
  if (entry.copyTotal <= 1) return entry.name;
  return `${entry.name} ${circled[entry.copyIndex - 1] ?? `#${entry.copyIndex}`}`;
}
